import mapValues from "lodash.mapvalues";
import round from "lodash.round";
import { v4 as uuid } from "uuid";

import { type Ingredient, type IngredientMeasure, type Nutrients } from "@fitness-app/data-models/entities/Ingredient";

import { type AddIngredientFormModel } from "~/modules/Nutrition/AddIngredientForm/types";

export const calculateNutrientsToDefault = (nutrients: Nutrients, packageSize = 100): Nutrients =>
  mapValues(nutrients, (value) =>
    typeof value === "number" ? round((value / packageSize) * 100, 1) : null,
  ) as Nutrients;

export function transformToProductTypesArray(booleanObject: Ingredient): AddIngredientFormModel["productTypes"] {
  const productTypes: AddIngredientFormModel["productTypes"] = [];

  if (booleanObject.isDairyFree) productTypes.push("isDiaryFree");
  if (booleanObject.isGlutenFree) productTypes.push("isGlutenFree");
  if (booleanObject.isHighProtein) productTypes.push("isHighProtein");
  if (booleanObject.isVegan) productTypes.push("isVegan");
  if (booleanObject.isVegetarian) productTypes.push("isVegetarian");

  return productTypes;
}

/** The edit form's model for an existing product - the inverse of `transformUpdateModelToEntity`. */
export const transformEntityToEditModel = (ingredient: Ingredient): AddIngredientFormModel => {
  const editableMeasures = ingredient.measures
    .filter((measure) => measure.id !== 1)
    .map((measure) => ({
      value: measure.id,
      weightPerUnit: measure.weightPerUnit,
      unit: measure.unit as "ml" | "grams",
    }));

  return {
    nutrients: { ...ingredient.nutrients, salt: ingredient.nutrients.salt ?? 0 },
    image: ingredient.image,
    name: ingredient.name,
    brand: ingredient.brand,
    barCode: ingredient.barCode,
    manufacturer: ingredient.manufacturer,
    calories: ingredient.nutrients.calories,
    liquid: ingredient.liquid ?? false,
    nutritionPer: "grams",
    caloriesUnit: ingredient.unit === "gram" ? "grams" : "millilitres",
    productTypes: transformToProductTypesArray(ingredient),
    // Kategoria jest w formularzu polem wymaganym — wstawienie tu `null`
    // kazało przy każdej edycji wybierać ją od nowa, a pomyłka podmieniała
    // kategorię produktu, który tylko miał poprawione makro.
    mainCategory: ingredient.mainCategory,
    rawIngredients: ingredient.rawIngredients || "",
    // Gramowa miara (id 1) jest ukryta w formularzu, a ~7,7 tys. produktów
    // w katalogu nie ma żadnej innej. Pusta lista wpada na walidator
    // „At least 1 level" i blokuje zapis, więc dokładamy wiersz opakowania
    // — ten sam, od którego startuje ścieżka dodawania.
    measures: editableMeasures.length
      ? editableMeasures
      : [{ value: 9, weightPerUnit: ingredient.packageSize ?? 0, unit: "grams" as const }],
  };
};

export const transformUpdateModelToEntity = (
  model: AddIngredientFormModel,
  measuresMap: Record<string, IngredientMeasure>,
  ingredient: Ingredient,
  canApprove: boolean,
): Ingredient => {
  const nutrients = calculateNutrientsToDefault({ ...model.nutrients, calories: model.calories }, 100);

  const measures: Ingredient["measures"] = model.measures.map((measure) => {
    const { value, weightPerUnit, unit } = measure;
    const measureFromMap = measuresMap[value];

    return {
      weightPerUnit,
      unit,
      id: measureFromMap?.id || 0,
      name: measureFromMap?.name || "",
      energyPerUnit: round(measure.weightPerUnit * (nutrients.calories / 100)),
    };
  });

  const defaultMeasure = ingredient.measures.find((measure) => measure.id === 1);

  if (defaultMeasure && !measures.find((measure) => measure.id === 1)) {
    measures.unshift(defaultMeasure);
  }

  return {
    ...ingredient,
    nutrients: {
      ...nutrients,
      digestibleCarbs: nutrients.fiber ? nutrients.carbohydrates - nutrients.fiber : nutrients.carbohydrates,
      carbohydrateExchangers: round(nutrients.carbohydrates / 10, 1),
    },
    measures,
    // Pierwsza miara to opakowanie/porcja — ścieżka dodawania trzyma z niej
    // `packageSize`, więc edycja musi robić to samo, inaczej wpis rozjeżdża się
    // po zmianie gramatury opakowania.
    packageSize: model.measures[0]?.weightPerUnit ?? ingredient.packageSize,
    calories: nutrients.calories,
    isDairyFree: model.productTypes.includes("isDiaryFree"),
    isGlutenFree: model.productTypes.includes("isGlutenFree"),
    isHighProtein: model.productTypes.includes("isHighProtein"),
    isVegan: model.productTypes.includes("isVegan"),
    isVegetarian: model.productTypes.includes("isVegetarian"),
    mainCategory: model.mainCategory,
    rawIngredients: model.rawIngredients,
    name: model.name,
    brand: model.brand,
    manufacturer: model.manufacturer,
    barCode: model.barCode,
    image: model.image,
    // Zatwierdzanie to osobne uprawnienie i osobny trigger w bazie:
    // `guard_ingredient_verification` rzuca 42501, gdy `verified` się zmienia,
    // a aktor nie ma `nutrition.ingredients.approve` w scope ALL. Edytująca bez
    // tego prawa zostawia flagę taką, jaka jest — inaczej każda poprawka makro
    // na niezweryfikowanym produkcie kończyłaby się błędem zapisu.
    verified: canApprove ? true : ingredient.verified,
    updatedAt: new Date().toISOString(),
    rating: 3,
  };
};

export const transformModelToEntity = (
  model: AddIngredientFormModel,
  measuresMap: Record<string, IngredientMeasure>,
  userId?: string,
): Ingredient => {
  const { nutritionPer, caloriesUnit, productTypes, ...rest } = model;
  const nutrients = calculateNutrientsToDefault(
    { ...model.nutrients, calories: model.calories },
    nutritionPer === "piece" ? (model.measures[0]?.weightPerUnit ?? 100) : 100,
  );

  return {
    ...rest,
    category: null,
    createdBy: userId || null,
    groups: [],
    nutrients: {
      ...nutrients,
      digestibleCarbs: nutrients.fiber ? nutrients.carbohydrates - nutrients.fiber : nutrients.carbohydrates,
      carbohydrateExchangers: round(nutrients.carbohydrates / 10, 1),
    },
    calories: nutrients.calories,
    packageSize: model.measures[0]?.weightPerUnit ?? null,
    isDairyFree: model.productTypes.includes("isDiaryFree"),
    isGlutenFree: model.productTypes.includes("isGlutenFree"),
    isHighProtein: model.productTypes.includes("isHighProtein"),
    isSuperFood: false,
    isVegan: model.productTypes.includes("isVegan"),
    isVegetarian: model.productTypes.includes("isVegetarian"),
    mainCategory: model.mainCategory,
    measures: model.measures.map((measure) => {
      const { value, weightPerUnit, unit } = measure;
      const measureFromMap = measuresMap[value];

      return {
        weightPerUnit,
        unit,
        id: measureFromMap?.id || 0,
        name: measureFromMap?.name || "",
        energyPerUnit: round(measure.weightPerUnit * (nutrients.calories / 100)),
      };
    }),
    price: null,
    rating: 3,
    rawIngredients: model.rawIngredients,
    source: "owner_app",
    tags: [],
    type: "ingredient",
    unit: caloriesUnit === "grams" ? "gram" : "ml",
    updatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    verified: false,
    id: uuid(),
  };
};
